import { formatDistanceToNowStrict, intervalToDuration } from 'date-fns';
import { Fragment, useState } from 'react';

import { useInterval } from '#utils/use-interval.js';

import type { ReactNode } from 'react';

/** Delay before the timestamp tooltip first appears, in ms. */
export const TIMESTAMP_TOOLTIP_ENTER_DELAY_MS = 500;

/** Delay before the timestamp tooltip re-appears on adjacent hovers, in ms. */
export const TIMESTAMP_TOOLTIP_ENTER_NEXT_DELAY_MS = 100;

/**
 * Subscribes to a relative-date label for `date` that re-formats every second
 * so phrases like "5 minutes ago" or "in 2d 3h" stay current while the user
 * looks at them. Returns `null` when `date` is missing so callers can keep the
 * hook call unconditional and branch on the result.
 *
 * @param date Date to format relative to now, or null/undefined for no label.
 * @param precise When true, render compact unit-by-unit form ("5m 30s ago"); otherwise the loose phrase ("5 minutes ago").
 */
export function useRelativeLabel(date: Date | null | undefined, precise: boolean): ReactNode {
	const [label, setLabel] = useState<ReactNode>(() => formatRelativeDate(date, precise));
	useInterval(() => setLabel(formatRelativeDate(date, precise)), 1000);
	return label;
}

/**
 * Formats `date` as a relative phrase such as "5 minutes ago" or, when
 * `precise` is true, a compact unit string like "5m 30s ago". Returns `null`
 * when `date` is missing.
 */
export function formatRelativeDate(date: Date | null | undefined, precise: boolean): ReactNode {
	if (date === null || date === undefined) {
		return null;
	}

	if (!precise) {
		return formatDistanceToNowStrict(date, { addSuffix: true });
	}

	const now = new Date();
	const isFuture = date > now;
	const duration = intervalToDuration({ start: isFuture ? now : date, end: isFuture ? date : now });

	const unitDefs = [
		{ value: duration.years ?? 0, suffix: 'y', pad: false },
		{ value: duration.months ?? 0, suffix: 'mo', pad: false },
		{ value: (duration.weeks ?? 0) * 7 + (duration.days ?? 0), suffix: 'd', pad: false },
		{ value: duration.hours ?? 0, suffix: 'h', pad: true },
		{ value: duration.minutes ?? 0, suffix: 'm', pad: true },
		{ value: duration.seconds ?? 0, suffix: 's', pad: true },
	];

	const firstNonzeroIndex = unitDefs.findIndex(({ value }) => value > 0);
	const relevantUnits = firstNonzeroIndex === -1 ? unitDefs.slice(-1) : unitDefs.slice(firstNonzeroIndex);

	const formatted = relevantUnits.map(({ value, suffix, pad }, index) => {
		const formattedValue = index > 0 && pad ? String(value).padStart(2, '0') : String(value);
		return (
			<Fragment key={suffix}>
				{index > 0 && ' '}
				{formattedValue}
				<span className='text-gray-a11'>{suffix}</span>
			</Fragment>
		);
	});

	return isFuture ? <>in {formatted}</> : <>{formatted} ago</>;
}

/**
 * Formats `date` as a full calendar value for tooltip use, respecting the
 * user's locale (weekday, day, month, year, HH:MM:SS).
 */
export function formatTooltipDate(date: Date): string {
	return date.toLocaleString(undefined, {
		weekday: 'short',
		day: 'numeric',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	});
}
